import { httpTransactionAnalytics } from "api/admin/stats";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Search from "components/ui/Search";
import Toggle from "components/ui/Toggle";
import { DateTime } from "luxon";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import currency, { cryptoFormat, moneyFormat } from "utilities/currency";
import { cryptos } from "utilities/wallets";

const filters = [
  {
    label: "Naira",
    value: "naira",
  },
  {
    label: "Buy & Sell",
    value: "buy-sell",
  },
  {
    label: "Send & Receive",
    value: "send-receive",
  },
  {
    label: "Swap",
    value: "swap",
  },
  {
    label: "Recurring Buy",
    value: "recurring",
  },
  {
    label: "OTC",
    value: "otc",
  },
  {
    label: "Virtual Cards",
    value: "virtual-card",
  },
];

const TransactionAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(filters[0].label);
  const [date, setDate] = useState(DateTime.local().toISODate());

  const filterValue = filters.find((f) => f.label === filter)?.value || "naira";

  const { data: transactionData } = useQuery(
    ["transactionAnalytics", filterValue, date],
    () => httpTransactionAnalytics(filterValue, date),
    {
      initialData: {},
      onError: (err) => {
        navigate(-1);
      },
    },
  );

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 mb-10 text-left">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-2xl">Transaction analytics</h3>
          <div className="w-3/6">
            <Search type="user" />
          </div>
        </div>

        <div className="my-10">
          <Toggle
            data={filters.map((filter) => filter.label)}
            value={filter}
            setValue={setFilter}
          />
        </div>

        <div>
          <span className="ml-5 text-sm">Filter by date:</span>
          <input
            type="date"
            className="py-1 ml-2 border-gray-400 rounded"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap mt-20 gap-y-14">
          <>
            {filterValue === "naira" && (
              <>
                <div className="w-4/12 rounded-lg">
                  <h4 className="mb-2 text-sm font-light">
                    Total Naira deposit
                  </h4>
                  <p className="text-2xl font-medium">
                    <span className="text-2xl">{currency("ngn")}</span>{" "}
                    {`${moneyFormat(transactionData?.["totalDeposits"] || 0)}`}
                  </p>
                </div>
                <div className="w-4/12 rounded-lg">
                  <h4 className="mb-2 text-sm font-light">
                    Total Naira withdrawal
                  </h4>
                  <p className="text-2xl font-medium">
                    <span className="text-2xl">{currency("ngn")}</span>{" "}
                    {`${moneyFormat(
                      transactionData?.["totalWithdrawals"] || 0,
                    )}`}
                  </p>
                </div>
                <div className="w-4/12 rounded-lg">
                  <h4 className="mb-2 text-sm font-light">
                    Total Naira earned
                  </h4>
                  <p className="text-2xl font-medium">
                    <span className="text-2xl">{currency("ngn")}</span>{" "}
                    {`${moneyFormat(transactionData?.["totalEarnings"] || 0)}`}
                  </p>
                </div>
                <div className="w-4/12 rounded-lg">
                  <h4 className="mb-2 text-sm font-light">No of deposits</h4>
                  <p className="text-2xl font-medium">
                    {`${transactionData?.["deposit"] || 0}`}
                  </p>
                </div>
                <div className="w-4/12 rounded-lg">
                  <h4 className="mb-2 text-sm font-light">No of withdrawals</h4>
                  <p className="text-2xl font-medium">
                    {`${transactionData?.["withdrawal"] || 0}`}
                  </p>
                </div>
                <div className="w-4/12 rounded-lg">
                  <h4 className="mb-2 text-sm font-light">No of earnings</h4>
                  <p className="text-2xl font-medium">
                    {`${transactionData?.["earning"] || 0}`}
                  </p>
                </div>
              </>
            )}
            {filterValue === "buy-sell" && (
              <div>
                <div className="flex gap-5 -mt-10">
                  <div className="px-10 py-5 pr-20 text-left bg-orange-100 rounded-lg">
                    <h4 className="mb-1 text-sm font-light">Total Bought</h4>
                    <p className="text-lg font-medium">
                      {currency("usd")}{" "}
                      {`${moneyFormat(transactionData?.["totalBuy"] || 0)}`}
                    </p>
                  </div>
                  <div className="px-10 py-5 pr-20 text-left bg-orange-100 rounded-lg">
                    <h4 className="mb-1 text-sm font-light">Total Sold</h4>
                    <p className="text-lg font-medium">
                      {currency("usd")}{" "}
                      {`${moneyFormat(transactionData?.["totalSell"] || 0)}`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center w-full mt-10">
                  {cryptos.map((crypto) => (
                    <div
                      key={crypto}
                      className="px-10 py-8 border-b-2 odd:border-r odd:border-r-orange-100 border-b-orange-50"
                    >
                      <div className="flex items-center justify-between w-full">
                        <h4 className="font-bold">{crypto.toUpperCase()}</h4>
                        <i className="p-1 rounded fa fa-bitcoin bg-orange-50"></i>
                      </div>
                      <div className="flex flex-wrap gap-y-5">
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">Total Buy</h4>
                          <p className="text-xl font-medium">
                            {`${cryptoFormat(
                              transactionData?.[crypto]?.["totalBuy"] || 0,
                            )}`}{" "}
                            {crypto.toUpperCase()}
                          </p>
                        </div>
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            Total Sell
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${cryptoFormat(
                              transactionData?.[crypto]?.["totalSell"] || 0,
                            )}`}{" "}
                            {crypto.toUpperCase()}
                          </p>
                        </div>
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">No of Buy</h4>
                          <p className="text-xl font-medium">
                            {`${transactionData?.[crypto]?.["buy"] || 0}`}{" "}
                          </p>
                        </div>
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            No of Sell
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${transactionData?.[crypto]?.["sell"] || 0}`}{" "}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filterValue === "send-receive" && (
              <div>
                <div className="flex gap-5 -mt-10">
                  <div className="px-10 py-5 pr-20 text-left bg-orange-100 rounded-lg">
                    <h4 className="mb-1 text-sm font-light">Total Sent</h4>
                    <p className="text-lg font-medium">
                      {currency("usd")}{" "}
                      {`${moneyFormat(transactionData?.["totalSend"] || 0)}`}
                    </p>
                  </div>
                  <div className="px-10 py-5 pr-20 text-left bg-orange-100 rounded-lg">
                    <h4 className="mb-1 text-sm font-light">Total Received</h4>
                    <p className="text-lg font-medium">
                      {currency("usd")}{" "}
                      {`${moneyFormat(transactionData?.["totalReceive"] || 0)}`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center w-full mt-10">
                  {cryptos.map((crypto) => (
                    <div className="px-10 py-8 border-b-2 odd:border-r odd:border-r-orange-100 border-b-orange-50">
                      <div className="flex items-center justify-between w-full">
                        <h4 className="font-bold">{crypto.toUpperCase()}</h4>
                        <i className="p-1 rounded fa fa-bitcoin bg-orange-50"></i>
                      </div>
                      <div className="flex flex-wrap gap-y-5">
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            Total Sent
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${cryptoFormat(
                              transactionData?.[crypto]?.["totalSend"] || 0,
                            )}`}{" "}
                            {crypto.toUpperCase()}
                          </p>
                        </div>
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            Total Receive
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${cryptoFormat(
                              transactionData?.[crypto]?.["totalReceive"] || 0,
                            )}`}{" "}
                            {crypto.toUpperCase()}
                          </p>
                        </div>
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            No of Send
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${transactionData?.[crypto]?.["send"] || 0}`}{" "}
                          </p>
                        </div>
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            No of Receive
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${
                              transactionData?.[crypto]?.["receive"] || 0
                            }`}{" "}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filterValue === "swap" && (
              <div>
                <div className="flex gap-5 -mt-10">
                  <div className="px-10 py-5 pr-20 text-left bg-orange-100 rounded-lg">
                    <h4 className="mb-1 text-sm font-light">Total Swapped</h4>
                    <p className="text-lg font-medium">
                      {currency("usd")}{" "}
                      {`${moneyFormat(transactionData?.["totalSwap"] || 0)}`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center w-full mt-10">
                  {cryptos.map((crypto) => (
                    <div
                      key={crypto}
                      className="px-10 py-8 border-b-2 odd:border-r odd:border-r-orange-100 border-b-orange-50"
                    >
                      <div className="flex items-center justify-between w-full">
                        <h4 className="font-bold">{crypto.toUpperCase()}</h4>
                        <i className="p-1 rounded fa fa-bitcoin bg-orange-50"></i>
                      </div>
                      <div className="flex flex-wrap gap-y-5">
                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            Total Swapped
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${cryptoFormat(
                              transactionData?.[crypto]?.["totalSwap"] || 0,
                            )}`}{" "}
                            {crypto.toUpperCase()}
                          </p>
                        </div>

                        <div className="w-6/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">
                            No of Swap
                          </h4>
                          <p className="text-xl font-medium">
                            {" "}
                            {`${transactionData?.[crypto]?.["swap"] || 0}`}{" "}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filterValue === "recurring" && (
              <div>
                <div className="flex flex-wrap items-center w-full mt-2">
                  {cryptos.map((crypto) => (
                    <div className="px-10 py-8 border-b-2 odd:border-r odd:border-r-orange-100 border-b-orange-50">
                      <div className="flex items-center justify-between w-full">
                        <h4 className="font-bold">{crypto.toUpperCase()}</h4>
                        <i className="p-1 rounded fa fa-bitcoin bg-orange-50"></i>
                      </div>
                      <div className="flex flex-wrap gap-y-5">
                        <div className="w-7/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">Total Buy</h4>
                          <p className="text-xl font-medium">
                            {currency("ngn")}{" "}
                            {`${moneyFormat(
                              transactionData?.[crypto]?.["totalBuy"] || 0,
                            )}`}
                          </p>
                        </div>

                        <div className="w-7/12 rounded-lg">
                          <h4 className="mb-2 text-sm font-light">No of Buy</h4>
                          <p className="text-xl font-medium">
                            {`${transactionData?.[crypto]?.["buy"] || 0}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {filterValue === "otc" && (
              <div className="w-full">
                <div className="flex flex-wrap gap-5 -mt-10">
                  <Metric
                    label="Total OTC Volume"
                    value={`${currency("ngn")} ${moneyFormat(transactionData?.totalVolume || 0)}`}
                  />
                  <Metric
                    label="Completed Trades"
                    value={transactionData?.completedTrades || 0}
                  />
                  <Metric
                    label="Failed Trades"
                    value={transactionData?.failedTrades || 0}
                  />
                </div>
                <div className="grid gap-5 mt-10 md:grid-cols-3">
                  {Object.entries(transactionData?.coins || {}).map(
                    ([coin, data]: [string, any]) => (
                      <div className="p-5 rounded bg-orange-50" key={coin}>
                        <h4 className="font-bold">{coin.toUpperCase()}</h4>
                        <p className="mt-3 text-sm">
                          Volume: {currency("ngn")}{" "}
                          {moneyFormat(data.volume || 0)}
                        </p>
                        <p className="mt-1 text-sm">
                          Trades: {data.count || 0}
                        </p>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
            {filterValue === "virtual-card" && (
              <div className="w-full">
                <div className="flex flex-wrap gap-5 -mt-10">
                  <Metric
                    label="Total Card Spend"
                    value={`${currency("usd")} ${moneyFormat(transactionData?.totalSpend || 0)}`}
                  />
                </div>
                <div className="grid gap-5 mt-10 md:grid-cols-3">
                  {Object.entries(
                    transactionData?.merchantCategories || {},
                  ).map(([category, amount]) => (
                    <div className="p-5 rounded bg-orange-50" key={category}>
                      <h4 className="font-bold">{category}</h4>
                      <p className="mt-3 text-sm">
                        {currency("usd")} {moneyFormat(Number(amount) || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        </div>
      </Main>
    </Container>
  );
};

const Metric: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="px-10 py-5 pr-20 text-left bg-orange-100 rounded-lg">
    <h4 className="mb-1 text-sm font-light">{label}</h4>
    <p className="text-lg font-medium">{value}</p>
  </div>
);

export default TransactionAnalytics;
